import React, {useEffect, useState} from 'react'
import { useLocation } from 'react-router';
import FixedLoader from "../../../components/FixedLoader/FixedLoader";
import TalentNavbar from "../../../components/TalentNavbar";

import dayjs from "dayjs";
import isToday from "dayjs/plugin/isToday";

import {usePersistedState} from "../../../hooks";
import {HYGIENIST_TYPES, HygienistProps, PositionProps, SkillsProps, UserAccountProps, Type} from "utils/types";
import {entities} from "../../../reduxStore";

import {AboutMe} from "./components/AboutMe";
import {Profile} from "./components/Profile";
import {Qualification} from "./components/Qualification";
import Experience from "./components/Experience";
import Api from "../../../api";
import styles from "../../dentist/PostJobPage/PostJob.module.scss";
import {CrossButtonBlack} from "../../../components/Buttons/CrossButton";
import clsx from "clsx";
import {StyledButton} from "../../../components/Buttons/DefaultButton/DefaultButton.styles";
import {SimpleModal} from "../../../components/Modal";
import {exitReact, formatErrorMessage} from "../../../utils";
import {useHistory} from "react-router";
import * as ls from "utils/localStorage";
import { HygienistSkill } from 'utils/constants';
import {CoverLetter} from "./components/CoverLetter";
import {ContactLinkMessage, ContactLinkText, getAddressChangeLimit} from "./addressChangeLimit";
import { requestPhoneVerification } from "utils/phoneVerificationGate";
import usePhoneVerificationNavigator from "hooks/usePhoneVerificationNavigator";
import { useForm } from 'react-hook-form'
import { joiResolver } from '@hookform/resolvers/joi'
import Joi from 'joi'
import { FormRadioInputGroup, RadioInput } from "components/Inputs/RadioInput";
import { FormMaterialSelector } from "components/Selector";
import { isWritingAssistV2Enabled } from "utils/writingAssistV2";

dayjs.extend(isToday)

// @ts-ignore
const TempStars = window.TempStars as any;

const normalizePhoneDigits = (phone: string | number | undefined | null): string => {
    const d = String(phone ?? '').replace(/\D/g, '')
    if (d.length === 11 && d.startsWith('1')) return d.slice(1)
    return d
}


interface LocationState {
    isOpenPositionModal?: boolean;
    isOpenQualificationModal?: boolean;
    scrollToLanguages?: boolean;
    isOpenCoverLetterModal?: boolean;
    returnToJobApplication?: {
        job: any;
        includeCoverLetter: boolean;
    };
}

const ProfilePage = () => {
    const history = useHistory();
    usePhoneVerificationNavigator()
    const location = useLocation<LocationState>();
    const hygienist = usePersistedState<{
        skills: HygienistSkill[],
    } & HygienistProps>({
        entityName: entities.hygienist
    })[0];

    const setHygienist = usePersistedState({
        entityName: entities.hygienist,
    })[1]

    const [about, setAbout] = useState<string>(hygienist?.about || '');
    const [isAboutChanged, setIsAboutChanged] = useState<boolean>(false);
    const [coverLetter, setCoverLetter] = useState<string>(hygienist?.baseCoverLetter || '');
    const [isCoverLetterChanged, setIsCoverLetterChanged] = useState<boolean>(false);
    const [positionsObj, setPositionsObj] = useState<PositionProps[]>([])
    const [loading, setLoading] = useState(false)
    const [autoBookLoading, setAutoBookLoading] = useState<boolean>(false)
    const [autoBookError, setAutoBookError] = useState<string>('')
    const [autoBookEditOpen, setAutoBookEditOpen] = useState<boolean>(false)
    const [avgRate, setAvgRate] = useState<number | null>(null)
    const [autoBookPrefill, setAutoBookPrefill] = useState<{ hourlyRate?: number; maxDistance?: 25|50|75|110; minShiftLength?: 4|5|6|7|8 } | null>(null)
    const [isOpenProfile, setOpenProfile] = useState<boolean>(true);
    const [isOpenAboutModal, setOpenAboutModal] = useState<boolean>(false);
    const [isOpenCoverLetterModal, setOpenCoverLetterModal] = useState<boolean>(location.state?.isOpenCoverLetterModal || false);
    const [isOpenSuccessModal, setOpenSuccessModal] = useState<boolean>(false);
    const [saveErrorMessage, setSaveErrorMessage] = useState<string | null>(null);
    // Set when the save error carries a contact link (address-change limit).
    const [saveErrorContact, setSaveErrorContact] = useState<ContactLinkMessage | null>(null);
    const closeSaveError = () => {
        setSaveErrorMessage(null)
        setSaveErrorContact(null)
    }
    const [isOpenQualificationModal, setOpenQualificationModal] = useState<boolean>(location.state?.isOpenQualificationModal || false);
    const [isOpenPositionModal, setOpenPositionModal] = useState(
        location.state?.isOpenPositionModal || false
    );
    const isHygienistOrAssistant =
        hygienist?.type === HYGIENIST_TYPES.HYGIENIST || hygienist?.type === HYGIENIST_TYPES.ASSISTANT;

    // Remember whether we need to auto-scroll to Languages inside the Qualification modal
    const autoScrollLanguagesRef = React.useRef<boolean>(!!location.state?.scrollToLanguages);
    
    // Remember if we came from a job application
    const returnToJobApplicationRef = React.useRef(location.state?.returnToJobApplication);

    useEffect(() => {
        if (hygienist) {
            setOpenProfile(!isOpenAboutModal && !isOpenPositionModal && !isOpenQualificationModal && !isOpenCoverLetterModal);
        }
    }, [hygienist, isOpenAboutModal, isOpenPositionModal, isOpenQualificationModal, isOpenCoverLetterModal]);

    useEffect(() => {
        if (!isAboutChanged && !isOpenAboutModal) {
            setAbout(hygienist?.about || '');
        }
    }, [hygienist?.about, isAboutChanged, isOpenAboutModal]);

    useEffect(() => {
        if (!isCoverLetterChanged && !isOpenCoverLetterModal) {
            setCoverLetter(hygienist?.baseCoverLetter || '');
        }
    }, [hygienist?.baseCoverLetter, isCoverLetterChanged, isOpenCoverLetterModal]);

    useEffect(() => {
        const fetchWorkExperience = async () => {
            try {
                setLoading(true)
                const workExperience = await Api.getWorkExperience()
                if (workExperience.length) {
                    setPositionsObj(workExperience)
                }
                setLoading(false)
            } catch (e) {
                console.log('Error fetching work experience', e)
            }
        }
        fetchWorkExperience()
    }, []);

    // Ensure freshest snapshot (includes GlobalSettings/DailySettings when InstabookActivated = 1)
    useEffect(() => {
        let isSubscribed = true
        const refreshUserSnapshot = async () => {
            try {
                const user = await Api.getUser()
                if (!isSubscribed) return
                ls.set<UserAccountProps>('userAccount', user as UserAccountProps)
                setHygienist(user?.hygienist)
            } catch (e) {
                // noop: non-blocking refresh
            }
        }
        refreshUserSnapshot()
        return () => { isSubscribed = false }
    }, [])

    // Load current Insta‑Book global settings (if activated) and avg rate options
    useEffect(() => {
        const loadAutoBook = async () => {
            try {
                if (!hygienist?.InstabookActivated) return;
                setAutoBookLoading(true)
                const snapshot = await Api.getHygienistSettings()
                const gs = snapshot?.GlobalSettings
                if (gs) {
                    setAutoBookPrefill({ hourlyRate: gs.hourlyRate, maxDistance: gs.maxDistance as 25|50|75|110, minShiftLength: gs.minShiftLength as 4|5|6|7|8 })
                }
                const type = (hygienist?.type as unknown as Type)
                if (hygienist?.postalCode && (type === 0 || type === 1)) {
                    const avg = await Api.getAvgRate({ type, postalCode: hygienist.postalCode })
                    setAvgRate(Number(avg?.avgRate) || null)
                } else {
                    setAvgRate(null)
                }
            } catch (e: any) {
                setAutoBookError(e?.message || 'Failed to load AutoBook settings')
            } finally {
                setAutoBookLoading(false)
            }
        }
        loadAutoBook()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hygienist?.InstabookActivated])


    const onSubmit = async (e: any) => {
        setLoading(true)
        closeSaveError()
        try {
            await Api.updateHygienistAccount({
                address: e.address,
                city: e.city,
                province: e.province,
                postalCode: e.postalCode,
                phone: e.phone,
                photoUrl: e.photoUrl,
                etransferEmail: e.eTransferEmail,
                about: about,
                baseCoverLetter: coverLetter,
                preferredName: Object.prototype.hasOwnProperty.call(e, 'preferredName')
                    ? (e.preferredName || null)
                    : undefined,
                WeeklyNewsletter: !!e.weeklyNewsletter ? 1 : 0,
                receivePostedShifts: !!e.receivePostedShifts ? 1 : 0,
                showForHire: e.showForHire,
            })
            // T2-7518: the server clears Verified itself when the digits change
            // (T2-7514), so the old { verified: 0 } follow-up call is gone.
            const phoneDigitsNew = normalizePhoneDigits(e.phone)
            const phoneDigitsOld = normalizePhoneDigits(hygienist?.phone)
            const phoneChanged = phoneDigitsNew.length >= 10 && phoneDigitsNew !== phoneDigitsOld
            const user = await Api.getUser()
            ls.set<UserAccountProps>('userAccount', user as UserAccountProps)
            setHygienist(user?.hygienist)
            TempStars?.User?.setCurrentUser?.(user)
            setIsAboutChanged(false)
            setIsCoverLetterChanged(false)

            if (phoneChanged && !user?.hygienist?.Verified) {
                // T2-7518: verify the new number straight away (the flow shows the
                // number just saved, from the refreshed current user).
                requestPhoneVerification()
            } else if (returnToJobApplicationRef.current && isCoverLetterChanged) {
                // Check if we need to return to job application
                // Navigate back to job application with the updated cover letter
                history.push({
                    pathname: 'jobPostDetailsPage',
                    state: {
                        job: returnToJobApplicationRef.current.job,
                        returnedFromCoverLetterCreation: true
                    }
                });
                returnToJobApplicationRef.current = undefined; // Clear the reference
            } else {
                setOpenSuccessModal(true)
            }
        } catch (e: any) {
            console.log('Error updating hygienist account', e)
            const addressChangeLimit = getAddressChangeLimit(e)
            if (addressChangeLimit) {
                setSaveErrorContact(addressChangeLimit)
                setSaveErrorMessage(addressChangeLimit.message)
                return
            }
            const parsed = formatErrorMessage(e)
            const msg =
                (typeof parsed === 'string' && parsed) ||
                e?.response?.data?.errors?.[0]?.msg ||
                e?.response?.data?.message ||
                e?.message ||
                'Could not save your profile. Please try again.'
            setSaveErrorMessage(typeof msg === 'string' ? msg : 'Could not save your profile. Please try again.')
        } finally {
            setLoading(false)
        }
    }

    const onSaveAbout = (about: string) => {
        setIsAboutChanged(true)
        setAbout(about)
    }

    const onSaveCoverLetter = (text: string) => {
        setIsCoverLetterChanged(true)
        setCoverLetter(text)
        
        // If we came from job application and this was done via the modal, 
        // close the modal and trigger profile save
        if (returnToJobApplicationRef.current && isOpenCoverLetterModal) {
            setOpenCoverLetterModal(false);
            // Trigger save after a brief delay to allow modal to close
            setTimeout(() => {
                // Call onSubmit with the current hygienist data
                if (hygienist) {
                    onSubmit({
                        address: hygienist.address,
                        city: hygienist.city,
                        province: hygienist.province,
                        postalCode: hygienist.postalCode,
                        phone: hygienist.phone,
                        photoUrl: hygienist.photoUrl,
                        eTransferEmail: hygienist.etransferEmail,
                        weeklyNewsletter: hygienist.WeeklyNewsletter,
                        receivePostedShifts: hygienist.receivePostedShifts,
                        showForHire: hygienist.showForHire,
                    });
                }
            }, 100);
        }
    }

    // -------- Insta‑Book Global Settings: form schema and helpers --------
    type DistanceKm = 25 | 50 | 75 | 110
    type MinShiftHours = 4 | 5 | 6 | 7 | 8

    interface GlobalSettingsForm {
        hourlyRate: number | ''
        maxDistance: DistanceKm | ''
        minShiftLength: MinShiftHours | ''
    }

    const autoBookSchema = Joi.object<GlobalSettingsForm>({
        hourlyRate: Joi.number().required(),
        maxDistance: Joi.number().valid(25, 50, 75, 110).required(),
        minShiftLength: Joi.number().valid(4, 5, 6, 7, 8).required(),
    })

    const {
        control: abControl,
        handleSubmit: abHandleSubmit,
        setValue: abSetValue,
        formState: { isValid: abIsValid },
    } = useForm<GlobalSettingsForm>({
        mode: 'onChange',
        resolver: joiResolver(autoBookSchema) as any,
        defaultValues: { hourlyRate: '', maxDistance: '', minShiftLength: '' } as any,
    })

    useEffect(() => {
        if (autoBookPrefill) {
            abSetValue('hourlyRate', autoBookPrefill.hourlyRate as any)
            abSetValue('maxDistance', autoBookPrefill.maxDistance as any)
            abSetValue('minShiftLength', autoBookPrefill.minShiftLength as any)
        }
    }, [autoBookPrefill, abSetValue])

    const rateOptions = React.useMemo(() => {
        const a = avgRate
        const type = hygienist?.type as unknown as Type
        if (!a || isNaN(a)) {
            const range = type === 1 ? [18, 40] : [30, 80]
            const list: number[] = []
            for (let r = range[0]; r <= range[1]; r++) list.push(r)
            return { list, recommended: null as number | null }
        }
        const spread = type === 1 ? 6 : 10
        const min = Math.max(0, Math.floor(a - spread))
        const max = Math.floor(a + spread)
        const list: number[] = []
        for (let r = min; r <= max; r++) list.push(r)
        return { list, recommended: Math.round(a) }
    }, [avgRate, hygienist?.type])

    const onSaveAutoBook = async (data: GlobalSettingsForm) => {
        setAutoBookError('')
        try {
            const payload = {
                hourlyRate: Number(data.hourlyRate),
                maxDistance: Number(data.maxDistance) as DistanceKm,
                minShiftLength: Number(data.minShiftLength) as MinShiftHours,
            }
            const hasExisting = !!(autoBookPrefill?.hourlyRate && autoBookPrefill?.maxDistance && autoBookPrefill?.minShiftLength)
            if (hasExisting) {
                await Api.updateInstabookSettings(payload)
            } else {
                await Api.setInstabookSettings(payload)
            }
            try { (window as any)?.TempStars?.Analytics?.track?.('Autobook_Settings_Saved_From_Profile', payload) } catch {}
            // Refresh user snapshot so menus and flows have latest
            const user = await Api.getUser()
            ls.set<UserAccountProps>('userAccount', user as UserAccountProps)
            setHygienist(user?.hygienist)
            setAutoBookPrefill({ hourlyRate: payload.hourlyRate, maxDistance: payload.maxDistance, minShiftLength: payload.minShiftLength })
            setAutoBookEditOpen(false)
        } catch (e: any) {
            setAutoBookError(e?.message || 'Failed to save AutoBook settings')
        }
    }

    const onExit = () => {
        setOpenSuccessModal(false)
        history.push('loading')
        if(isHygienistOrAssistant) {
            ;(window as any).TempStars?.App?.goReact('hygienistHome')
        } else {
            history.push('jobPostsList')
        }
    }

    const webPhotoHandler = (photoUrl: any) => {
        const file = photoUrl.files[0];
        const reader = new FileReader();
        // ... existing code ...
    }

    // Get user info for navbar
    const firstName = hygienist?.firstName || '';
    const lastName = hygienist?.lastName || '';
    const profilePhotoUrl = hygienist?.photoUrl || undefined;
    const isHygOrAsst = hygienist?.type === HYGIENIST_TYPES.HYGIENIST || 
                        hygienist?.type === HYGIENIST_TYPES.ASSISTANT;

    return (<>
        <TalentNavbar
            firstName={firstName}
            lastName={lastName}
            photoUrl={profilePhotoUrl}
            isHygOrAsst={isHygOrAsst}
            instabookActivated={hygienist?.InstabookActivated === 1}
            isTestAccount={(hygienist as any)?.isTest === 1}
            showAddShiftButton={false}
            showJobBoardButton={false}
            elevated
        />

        {/* T2-5791: avoid white screen when hygienist snapshot is not rehydrated yet after signup */}
        {!hygienist?.id ? (
            <FixedLoader loading />
        ) : null}

        {!!hygienist?.id && (<>
        <SimpleModal isOpen={!!saveErrorMessage}>
            <>
                <div className={styles.titleContainer}>
                    <div className={styles.popupTitle}>Could not save profile</div>
                    <CrossButtonBlack className={clsx("no-fastclick", styles.crossButtonDark)}
                                      onClick={closeSaveError}/>
                </div>
                <div className={styles.description}>
                    {saveErrorContact ? <ContactLinkText {...saveErrorContact} /> : saveErrorMessage}
                </div>
                <StyledButton
                    variant="contained"
                    size="large"
                    onClick={closeSaveError}
                >
                    OK
                </StyledButton>
            </>
        </SimpleModal>

        <SimpleModal isOpen={isOpenSuccessModal}>
            <>
                <div className={styles.titleContainer}>
                    <div className={styles.popupTitle}>Profile updated successfully!</div>
                    <CrossButtonBlack className={clsx("no-fastclick", styles.crossButtonDark)}
                                      onClick={() => setOpenSuccessModal(false)}/>
                </div>
                <StyledButton
                    variant="contained"
                    size="large"
                    onClick={onExit}
                >
                    Go Home
                </StyledButton>
                <StyledButton
                    variant="outlined"
                    size="large"
                    onClick={() => setOpenSuccessModal(false)}
                >
                    Close
                </StyledButton>
            </>
        </SimpleModal>


        {isOpenProfile
            ? <Profile
                isAboutChanged={isAboutChanged}
                isCoverLetterChanged={isCoverLetterChanged}
                workExperience={positionsObj}
                hygienist={{...hygienist, about, baseCoverLetter: coverLetter}}
                setOpenSuccessModal={() => setOpenSuccessModal(!isOpenSuccessModal)}
                setOpenAboutModal={() => setOpenAboutModal(!isOpenAboutModal)}
                setOpenCoverLetterModal={() => setOpenCoverLetterModal(!isOpenCoverLetterModal)}
                setOpenPositionModal={() => setOpenPositionModal(!isOpenPositionModal)}
                setOpenQualificationModal={() => setOpenQualificationModal(!isOpenQualificationModal)}
                onSubmit={onSubmit}
            />
            : null}

        {/* Insta‑Book Global Settings block (visible only when activated) */}
        {/* Removed bottom AutoBook edit block; top summary + edit entry is rendered inside the Profile modal content */}


        <AboutMe
            about={about}
            name={hygienist?.firstName}
            isOpen={isOpenAboutModal}
            onSaveAbout={onSaveAbout}
            onClose={() => setOpenAboutModal(false)}
        />

        {isWritingAssistV2Enabled() ? <CoverLetter
            coverLetter={coverLetter}
            name={hygienist?.firstName}
            isOpen={isOpenCoverLetterModal}
            onSaveCoverLetter={onSaveCoverLetter}
            onClose={() => {
                setOpenCoverLetterModal(false);
                // If we have a return path but no changes were made, go back
                if (returnToJobApplicationRef.current && !isCoverLetterChanged) {
                    history.push({
                        pathname: 'jobPostDetailsPage',
                        state: {
                            job: returnToJobApplicationRef.current.job,
                            returnedFromCoverLetterCreation: false
                        }
                    });
                    returnToJobApplicationRef.current = undefined;
                }
            }}
        /> : null}

        <Qualification
            isOpen={isOpenQualificationModal}
            hygienist={hygienist}
            autoScrollLanguages={autoScrollLanguagesRef.current}
            onSaveSuccess={(user) => {
                ls.set<UserAccountProps>('userAccount', user);
                TempStars?.User?.setCurrentUser?.(user);
                setHygienist(user.hygienist);
                setOpenQualificationModal(false);
                autoScrollLanguagesRef.current = false;
            }}
            onClose={() => {
                setOpenQualificationModal(false);
                autoScrollLanguagesRef.current = false; // reset after first use
            }}
        />

        <Experience
            positionsObj={positionsObj}
            setPositionsObj={setPositionsObj}
            country={hygienist?.country ?? 0}
            isOpen={isOpenPositionModal}
            onClose={() => setOpenPositionModal(false)}
            isHygienistOrAssistant={isHygienistOrAssistant}
        />

        <FixedLoader
            loading={loading}
        />
        </>)}
    </>)
}

export default ProfilePage;
